/* Az Bilinenler — gitar. Kurallar ezberlenir, nedenleri az bilinir. Blok türleri: ../lessons/blocks.ts
   Sources sit right above each fact as English comments: one line per claim, at least two sources each. */
import { cell, compare, go, h, list, p, play, table, tip } from '../lessons/blocks';
import type { Fact } from './types';

export const gitar: Fact[] = [
  // Sources:
  // - Standard tuning E2 A2 D3 G3 B3 E4: fourths except the major third G–B, so the two E strings are two
  //   octaves apart (4 x 5 + 4 = 24 semitones): https://en.wikipedia.org/wiki/Guitar_tunings ;
  //   https://www.acs.psu.edu/drussell/guitars/es-335.html ;
  //   https://opus4.kobv.de/opus4-matheon/files/675/7046_mathtune.pdf
  // - Fourths fit the hand (index on the root, little finger on the major third, the fourth on the next
  //   string; a chromatic scale over five frets, one less with open strings):
  //   https://www.fender.com/articles/setup/standard-tuning-how-eadgbe-came-to-be ;
  //   https://en.wikipedia.org/wiki/Regular_tuning
  // - Fifths (violin, mandolin) suit short necks; on a guitar they need seven-fret stretches and a top string
  //   prone to breaking: https://en.wikipedia.org/wiki/Regular_tuning ;
  //   https://sethares.engr.wisc.edu/alternatetunings/regulartunings.pdf ; Fender (above)
  // - Smaller intervals (major thirds) shrink the range: https://en.wikipedia.org/wiki/Regular_tuning ;
  //   Griewank, opus4.kobv.de (above)
  // - Fifth-fret tuning, fourth fret on the G string:
  //   https://www.dummies.com/article/academics-the-arts/music/instruments/guitar/how-to-tune-a-guitar-to-itself-using-the-fifth-fret-method-206627/ ;
  //   https://sethares.engr.wisc.edu/alternatetunings/alltunings.pdf
  {
    id: 'tel-araliklari',
    topic: 'gitar',
    title: 'Teller neden 5 yarım ses arayla akort edilir?',
    teaser: `Her telin 5. perdesi bir sonraki telin boş sesidir. Bu kuralın arkasında elinin boyu var.`,
    minutes: 3,
    blocks: [
      tip(
        'info',
        'Ezberlenen',
        `Bir telin 5. perdesi, bir sonraki ince telin boş sesini verir. Tek istisna 3. tel: orada 4. perde.`,
      ),
      p(
        `Standart akortta teller kalından inceye {{n:E2}}, {{n:A2}}, {{n:D3}}, {{n:G3}}, {{n:B3}} ve {{n:E4}} seslerini verir. Beş komşu tel çiftinin dördünde aradaki mesafe **5 yarım ses**, yani bir **tam 4'lü**dür.`,
      ),
      {
        t: 'fretboard',
        notes: 'E2 A2 D3 G3 B3 E4',
        from: 0,
        to: 5,
        caption: `Her boş telin sesi, bir kalın telin 5. perdesinde yeniden çıkar (3. telde 4. perde). Dokunarak dinle.`,
      },
      h(`Neden 5 yarım ses?`),
      p(
        `Elin bir pozisyonda aşağı yukarı beş perdelik bir alana yetişir: işaret parmağın bir notadayken serçe parmağın 4 yarım ses yukarısına, yani büyük 3'lüye uzanabilir. 5 yarım ses yukarısı ise bir sonraki teldedir. Böylece bir telden ötekine geçerken hiçbir nota atlanmaz; elini kaydırmadan bütün notalara sırayla basarsın.`,
      ),
      p(
        `İlk pozisyonda boş teller işi daha da kolaylaştırır: her telin 0–4. perdeleri, bir sonraki telin boş sesine kadar kesintisiz bir dizi oluşturur.`,
      ),
      play(`Kalın {{pc:E}} telinin 0–4. perdeleri, sonra {{pc:A}} teli: kesintisiz`, {
        seq: 'E2 F2 F#2 G2 G#2 A2 A#2 B2 C3 C#3 D3:2',
        bpm: 150,
      }),
      h(`Başka aralıklar olsaydı`),
      list([
        `**7 yarım ses (tam 5'li):** Keman ve mandolin böyle akort edilir. Kısa saplı bu çalgılarda sorun olmaz; gitarın uzun sapında ise aynı iş için yedi perdelik bir açıklık gerekirdi. Üstelik en ince tel çok yükseğe akort edilmek zorunda kalır ve kolayca koparırdı.`,
        `**4 yarım ses (büyük 3'lü):** El rahat ederdi, ama aynı altı tel daha dar bir ses alanını kaplardı.`,
      ]),
      {
        t: 'quiz',
        q: `5. telin 5. perdesi hangi boş telle aynı sesi verir?`,
        items: [
          { label: '5. tel, 5. perde', seq: 'D3' },
          { label: '4. tel, boş', seq: 'D3' },
          { label: '3. tel, boş', seq: 'G3' },
        ],
        options: ['4. tel', '3. tel', '6. tel'],
        answer: 0,
        explain: `5. tel boşken {{n:A2}} verir; 5 yarım ses yukarısı {{n:D3}}, yani 4. telin boş sesi.`,
        hint: `5. tel boşken {{n:A2}} verir. 5 yarım ses yukarı say.`,
      },
      tip(
        'success',
        'Kendin dene',
        `6. telin 5. perdesine bas, ardından 5. teli boş çal. İki ses aynıysa bu iki tel birbirine göre akortludur. Aynı karşılaştırmayı diğer tellerde de yap; 3. telde 5. değil 4. perdeye bas.`,
      ),
      go('Gitarın boş telleri dersi', 'nota.ders.n3'),
      go('Akort aletini aç', 'araclar.akort'),
    ],
  },
  // Sources:
  // - G–B is the only major third in standard tuning: https://en.wikipedia.org/wiki/Guitar_tunings ;
  //   https://opus4.kobv.de/opus4-matheon/files/675/7046_mathtune.pdf
  // - All fourths would give E A D G C F (top string F) and far fewer full six-string open major and minor
  //   chords: https://www.fender.com/articles/setup/standard-tuning-how-eadgbe-came-to-be ;
  //   https://sethares.engr.wisc.edu/alternatetunings/alltunings.pdf ; https://en.wikipedia.org/wiki/Guitar_tunings
  // - The two E strings two octaves apart: Griewank (above) ; https://hubguitar.com/fretboard/the-b-string-gap
  // - Renaissance lute tuned fourth-fourth-major third-fourth-fourth: https://en.wikipedia.org/wiki/Lute ;
  //   https://www.simpk.de/fileadmin/user_upload_sim/Dokumente/Museum/Sammlung/Fuehrungsblaetter_EN/FB_10_Laute_GB_pre.pdf
  // - Vihuela tuned like the lute: https://en.wikipedia.org/wiki/Vihuela ;
  //   https://www.thisisclassicalguitar.com/vihuela-history-and-style/
  // - A guitar with its 3rd string down to F# plays lute music: https://en.wikipedia.org/wiki/Lute ;
  //   https://www.thisisclassicalguitar.com/vihuela-history-and-style/
  // - A D G B E on the five-course guitars of the 16th century: https://en.wikipedia.org/wiki/History_of_the_classical_guitar ;
  //   Fender (above)
  // - Six single strings from the late 18th century: https://www.laguitarra-blog.com/2012/06/05/stalking-the-oldest-six-string-guitar/ ;
  //   https://en.wikipedia.org/wiki/Romantic_guitar
  // - Stanley Jordan and Tom Quayle tune in all fourths: https://en.wikipedia.org/wiki/Stanley_Jordan ;
  //   https://www.encyclopedia.com/education/news-wires-white-papers-and-books/jordan-stanley ;
  //   https://guitar.com/features/interviews/tom-quayle-all-4ths/ ; https://en.wikipedia.org/wiki/All_fourths_tuning
  // - Shapes shift one fret across G–B; octave shapes two frets vs three:
  //   https://www.guitarplayer.com/lessons/black-hole-of-confusion-or-wormhole-to-enlightenment-the-unique-relationship-between-the-g-and-b-strings ;
  //   https://hubguitar.com/fretboard/knowledge-octaves ;
  //   https://www.dummies.com/article/academics-the-arts/music/instruments/guitar/how-to-track-notes-and-play-songs-with-octaves-on-the-guitar-155166/
  {
    id: 'sol-si-araligi',
    topic: 'gitar',
    title: '{{pc:G}} ile {{pc:B}} telleri arası neden 4 yarım ses?',
    teaser: `Bütün teller 4'lü olsaydı en ince tel {{pc:F}} olurdu ve bildiğin açık akorların çoğu çalışmazdı.`,
    minutes: 4,
    blocks: [
      tip(
        'info',
        'Ezberlenen',
        `Gitarda kalıplar 3. telden 2. tele geçerken bir perde kayar. Oktav şekli de orada değişir.`,
      ),
      p(
        `Beş komşu tel çiftinden yalnızca biri 5 yarım ses kuralını bozar: 3. tel ({{n:G3}}) ile 2. tel ({{n:B3}}) arasında 4 yarım ses, yani bir **büyük 3'lü** vardır.`,
      ),
      compare([
        ['Standart akort', { seq: 'E2 A2 D3 G3 B3 E4:2', bpm: 150 }],
        [`Hepsi 4'lü olsaydı`, { seq: 'E2 A2 D3 G3 C4 F4:2', bpm: 150 }],
      ]),
      h(`Hepsi 4'lü olsaydı ne olurdu?`),
      list([
        `Teller {{pc:E}}, {{pc:A}}, {{pc:D}}, {{pc:G}}, {{pc:C}}, {{pc:F}} olurdu. Bugün en kalın ve en ince tel aynı notadır, tam iki oktav arayla {{pc:E}}; bu eşleşme kaybolurdu.`,
        `Açık akorların bildiğin şekilleri değişirdi. Altı telin birden çınladığı açık majör ve minör akorlar neredeyse kalmazdı.`,
      ]),
      compare([
        [`{{c:Em}} şekli, standart akortta`, { harm: 'E2 B2 E3 G3 B3 E4' }],
        [`Aynı şekil, hepsi 4'lü olsaydı`, { harm: 'E2 B2 E3 G3 C4 F4' }],
      ]),
      {
        t: 'chords',
        names: 'E A D G C',
        caption: `Bu tanıdık açık akor şekilleri, 3. ile 2. tel arasındaki büyük 3'lüyle birlikte çalışır. Dinlemek için şemaya dokun.`,
      },
      h(`Lavtadan gelen bir düzen`),
      p(
        `Telleri 4'lü aralıklarla dizip araya bir büyük 3'lü koymak gitardan çok daha eskidir. Rönesans lavtasının altı tel çifti 4'lü, 4'lü, büyük 3'lü, 4'lü, 4'lü aralıklarla akort edilirdi; vihuela da aynı düzeni kullanırdı. Gitar bu düzeni paylaşır, yalnızca büyük 3'lü lavtada ortada, gitarda 3. ile 2. tel arasındadır.`,
      ),
      compare([
        ['Gitar', { seq: 'E2 A2 D3 G3 B3 E4:2', bpm: 150 }],
        [`3. tel {{pc:F#}}: lavta düzeni`, { seq: 'E2 A2 D3 F#3 B3 E4:2', bpm: 150 }],
      ]),
      p(
        `Gitarın 3. telini yarım ses gevşetip {{pc:F#}} yaparsan lavtanın aralıklarını elde edersin; Rönesans lavta müziğinin çoğu bu akortla gitarda çalınabilir. Gitarın ince beş telindeki {{pc:A}}–{{pc:D}}–{{pc:G}}–{{pc:B}}–{{pc:E}} düzeni 16. yüzyılın beş sıra telli gitarlarında zaten vardı. Altı tek telli gitar 18. yüzyılın sonlarında yaygınlaştı.`,
      ),
      tip(
        'info',
        "Hepsi 4'lü çalanlar da var",
        `Stanley Jordan ve Tom Quayle gibi bazı gitaristler bütün tellerini 4'lü aralıklarla, {{pc:E}} {{pc:A}} {{pc:D}} {{pc:G}} {{pc:C}} {{pc:F}} olarak akort eder. Kalıplar sapın her yerinde aynı kalır, ama alışılmış açık akorların çoğundan vazgeçerler.`,
      ),
      h(`Bedeli: kalıplar bir perde kayar`),
      p(
        `Bir şekli 3. telden 2. tele geçirdiğinde, 2. teldeki notayı bir perde ileriden basman gerekir. Oktav şekli 6. ve 4. telde "iki tel ince, iki perde ileri"dir; 4. ve 2. telde ise **üç perde** ileri gider.`,
      ),
      {
        t: 'fretboard',
        notes: 'B3',
        from: 0,
        to: 5,
        caption: `{{n:B3}} iki yerde: 2. tel boşken ve 3. telin 4. perdesinde.`,
      },
      compare([
        ['3. tel 5. perde ve 2. tel boş', { harm: 'C4 B3' }],
        ['3. tel 4. perde ve 2. tel boş', { harm: 'B3 B3' }],
      ]),
      go('Sapta notaları bulmak dersi', 'nota.ders.n4'),
      go('4. ve 3. telde notaları bul', 'nota.egz.sap.3'),
    ],
  },
  // Sources:
  // - Each fret shortens the vibrating length by 2^(-1/12) (x 0.944, the 17.817 divisor) and the 12th fret
  //   halves the scale length, so frets get closer: https://en.wikipedia.org/wiki/Guitar ;
  //   https://liutaiomottola.com/formulae/fret.htm ;
  //   https://hub.yamaha.com/guitars/g-electric/why-do-the-spaces-between-frets-grow-narrower-down-the-neck
  // - Half the length sounds an octave higher: https://newt.phys.unsw.edu.au/jw/strings.html ;
  //   https://hyperphysics.gsu.edu/hbase/Waves/string.html
  // - The older "rule of 18" put frets slightly flat: https://liutaiomottola.com/PrevPubs/RuleOf18/RuleOf18.htm ;
  //   https://ar5iv.labs.arxiv.org/html/0906.0127 (Varieschi & Gower, Am. J. Phys. 78:47)
  // - Saddles sit slightly further back (compensation), so the 12th fret is a little short of the real
  //   middle: https://en.wikipedia.org/wiki/Guitar ; https://newt.phys.unsw.edu.au/jw/strings.html
  // - The table is computed from L x 2^(-n/12) for a 650 mm scale.
  {
    id: 'perdeler-sikilasir',
    topic: 'gitar',
    title: 'Perdeler neden gövdeye doğru sıklaşır?',
    teaser: `Her perde teli aynı oranda kısaltır. Bu yüzden aralar milim milim daralır ve 12. perde telin ortasına düşer.`,
    minutes: 3,
    blocks: [
      tip('info', 'Ezberlenen', `12. perde oktavdır: boş telle aynı nota, bir oktav yukarıda.`),
      p(
        `Telin sesi titreşen boyuna bağlıdır. Boy yarıya inince tel iki kat hızlı titreşir ve bir oktav yukarıdaki notayı duyarsın. 12. perde bu yüzden telin **ortasındadır**: üst eşikten 12. perdeye olan uzaklık, 12. perdeden köprüye olan uzaklığa eşittir.`,
      ),
      { t: 'neck', frets: 19 },
      h(`Neden eşit aralıklı değil?`),
      p(
        `Oktav 12 eşit yarım sese bölünür. Her yarım ses için titreşen boy, bir öncekinin yaklaşık **%94,4**'üne iner; yani her perde kalan boyu yaklaşık **%5,6** kısaltır. Uzun bir boyun %5,6'sı, kısa bir boyun %5,6'sından büyüktür. Bu yüzden ilk perdeler geniş, gövdeye yakın perdeler dardır.`,
      ),
      table(
        ['Perde', 'Titreşen boy', 'Üst eşikten uzaklık', 'Dinle'],
        [
          ['0 (boş tel)', '650,0 mm', '0 mm', cell({ seq: 'E2:2' })],
          ['1', '613,5 mm', '36,5 mm', cell({ seq: 'F2:2' })],
          ['5', '486,9 mm', '163,1 mm', cell({ seq: 'A2:2' })],
          ['7', '433,8 mm', '216,2 mm', cell({ seq: 'B2:2' })],
          ['12', '325,0 mm (yarısı)', '325,0 mm', cell({ seq: 'E3:2' })],
        ],
      ),
      p(
        `Tablo, titreşen boyu 650 mm olan bir telin değerleri. 1. perdenin aralığı 36,5 mm iken 12. perdenin aralığı yalnızca 19,3 mm'dir.`,
      ),
      compare([
        ['Boş tel: {{n:E2}}', { seq: 'E2:2' }],
        ['12. perde: {{n:E3}}', { seq: 'E3:2' }],
        ['İkisi birlikte', { harm: 'E2 E3' }],
      ]),
      tip(
        'info',
        'Ustaların hesabı',
        `Eski gitar yapımcıları perde yerini "18 kuralı"yla bulurdu: kalan boyu 18'e bölüp bir sonraki perdeyi koyarlardı. Bu, perdeleri biraz pes yerleştirir; bugün 17,817 sayısı kullanılır. Gerçek gitarlarda köprüdeki eşik, kalın tellerin sertliğini dengelemek için biraz geridedir; bu yüzden 12. perde telin gerçek ortasına çok yakın ama tam üstünde değildir.`,
      ),
      go('Oktavlar ve frekans dersi', 'nota.ders.n5'),
      go('Oktav avı', 'nota.egz.sap.6'),
    ],
  },
  // Sources:
  // - Touching the string lightly at 1/n of its length leaves harmonic n: 1/2 octave, 1/3 twelfth (octave and
  //   fifth), 1/4 double octave, 1/5 seventeenth, 1/6 nineteenth: https://newt.phys.unsw.edu.au/jw/strings.html ;
  //   https://en.wikipedia.org/wiki/String_harmonic ;
  //   https://hub.yamaha.com/guitars/bass/how-to-play-bass-guitar-harmonics
  // - Frets 5, 7, 12 sit (almost) at 3/4, 2/3, 1/2 of the length; the 1/5 and 1/6 nodes fall near the 4th and
  //   3rd frets (3.86, 3.16 by n = -12 log2(1 - x)): https://ar5iv.labs.arxiv.org/html/0906.0127 ;
  //   Yamaha bass harmonics (above)
  // - Harmonics are pure intervals: the 3rd is 1.96 cents above the fretted note, the 5th 13.69 cents below:
  //   https://en.wikipedia.org/wiki/String_harmonic ; https://en.wikipedia.org/wiki/Major_third ;
  //   https://www.musicmark.org.uk/a-common-approach/classical-guitar/area-b/pos-5/b2/ ;
  //   https://www.guitarmoderne.com/gear-2/tuning-the-guitar
  // - Tuning by 5th/7th-fret harmonics leaves each string a pure fourth, about 2 cents flat, and the error adds
  //   up (A -2, D -4, G -6 cents); it does not work for G–B: https://newt.phys.unsw.edu.au/jw/strings.html ;
  //   https://www.guyguitars.com/eng/handbook/Tuning/tuning.html ;
  //   https://www.classicalguitarcorner.com/how-to-tune-a-guitar-by-ear/
  // - Beat rate 415.30 - 412.03 = 3.3 Hz is computed.
  {
    id: 'flajole',
    topic: 'gitar',
    title: 'Flajole: telin içinde saklı notalar',
    teaser: `Teli basmadan 12., 7. ya da 5. perdenin tam üstüne hafifçe dokunup çalınca çan gibi bambaşka sesler duyarsın.`,
    minutes: 4,
    blocks: [
      tip('info', 'Ezberlenen', `Flajole 12., 7. ve 5. perdede çıkar.`),
      p(
        `Çalınan bir tel aynı anda birçok biçimde titreşir: bütün boyuyla, iki yarısıyla, üç parçasıyla, dört parçasıyla… Bu titreşimlere **harmonik** denir. Sesin rengini onlar belirler, ama genelde ayrı ayrı duyulmazlar.`,
      ),
      p(
        `Parmağını teli bastırmadan bir noktaya hafifçe değdirip çaldığında, o noktada kıpırdaması gereken titreşimler susar. Geriye yalnızca teli tam o noktadan bölen harmonikler kalır. Gitarcılar buna **flajole** der.`,
      ),
      table(
        ['Dokunduğun perde', 'Tel kaç parçaya bölünür?', 'Kalın {{pc:E}} telinde duyulan', 'Dinle'],
        [
          ['12', '2', `{{n:E3}}: 1 oktav`, cell({ seq: 'E3:3', inst: 'sine' })],
          ['7', '3', `{{n:B3}}: 1 oktav ve tam 5'li`, cell({ seq: 'B3+2c:3', inst: 'sine' })],
          ['5', '4', `{{n:E4}}: 2 oktav`, cell({ seq: 'E4:3', inst: 'sine' })],
          ['yaklaşık 3,9', '5', `{{n:G#4}}: 2 oktav ve büyük 3'lü`, cell({ seq: 'G#4-14c:3', inst: 'sine' })],
          ['yaklaşık 3,2', '6', `{{n:B4}}: 2 oktav ve tam 5'li`, cell({ seq: 'B4+2c:3', inst: 'sine' })],
        ],
      ),
      p(
        `12., 7. ve 5. perdeler teli 2, 3 ve 4 eşit parçaya böldüğü için flajole orada kolay çıkar. 3,9 ve 3,2 gibi kesirli değerler, noktanın iki perde telinin arasında olduğunu gösterir. 5., 4. ve 3. perdenin çevresindeki üç flajole birlikte telin majör akorunu verir: kalın {{pc:E}} telinde {{pc:E}}, {{pc:G#}} ve {{pc:B}}.`,
      ),
      play('Kalın {{pc:E}} teli ve ilk beş harmoniği, sırayla', {
        seq: 'E2:2 E3:2 B3+2c:2 E4:2 G#4-14c:2 B4+2c:2',
        bpm: 100,
        inst: 'piano',
      }),
      h(`Neden bazıları "falso"?`),
      p(
        `Harmonikler doğanın saf oranlarıdır; perdeler ise oktavı 12 eşit parçaya böler. 7. perdedeki flajole, perdeye basılan aynı notadan yaklaşık 2 sent tiz; 5 parçalık flajole ise yaklaşık 14 sent pestir. İkisini birlikte çalınca bu fark, saniyede yaklaşık 3 kez dalgalanan bir vuruş olarak duyulur.`,
      ),
      compare([
        ['Perdeye basılan {{n:G#4}}', { seq: 'G#4:3', inst: 'sine' }],
        ['5 parçalık flajole', { seq: 'G#4-14c:3', inst: 'sine' }],
        ['İkisi birlikte', { harm: 'G#4 G#4-14c', inst: 'sine' }],
      ]),
      tip(
        'warn',
        'Flajoleyle akort etmenin tuzağı',
        `5. ve 7. perde flajolelerini karşılaştırarak akort etmek yaygın bir yöntemdir. Ama her tel çifti saf bir 4'lüye oturur ve her adımda yaklaşık 2 sent pes kalır: kalın {{pc:E}} teline göre {{pc:A}} teli 2, {{pc:D}} teli 4, {{pc:G}} teli 6 sent pes olur. {{pc:G}} ile {{pc:B}} arasında ise bu yöntem doğrudan kullanılamaz. Son kontrolü akort aletiyle yap.`,
      ),
      go('Akort aletini aç', 'araclar.akort'),
      go('Oktavlar ve frekans dersi', 'nota.ders.n5'),
    ],
  },
  // Sources:
  // - Equal temperament: fifths and fourths are close to pure, thirds are not; the ET major third is 400 cents,
  //   13.69 wider than 5:4 (386.31); the ET fifth 2 cents narrower than pure:
  //   https://en.wikipedia.org/wiki/Equal_temperament ; https://en.wikipedia.org/wiki/Major_third ;
  //   https://newt.phys.unsw.edu.au/jw/tuning.html ; https://www.guitarmoderne.com/gear-2/tuning-the-guitar
  // - Pure major thirds (5:4) don't beat, tempered ones do: https://newt.phys.unsw.edu.au/jw/tuning.html ;
  //   https://arxiv.org/abs/2504.04919 (Mullin & Leinweber 2025)
  // - Players flatten the string carrying the third so one chord sounds sweeter, which puts other chords out:
  //   https://www.musicmark.org.uk/a-common-approach/classical-guitar/area-b/pos-5/b2/ ;
  //   https://arxiv.org/abs/2504.04919 ; https://www.petersontuners.com/myinstrument/electric ("Sweetened Tuning")
  {
    id: 'esit-tamperaman',
    topic: 'gitar',
    title: 'Akortlu gitarda akorlar neden biraz "falso"?',
    teaser: `Perdeler 12 eşit yarım sese göre dizilir. Her tonda çalabilmenin bedeli: büyük 3'lü, saf hâlinden 14 sent geniştir.`,
    minutes: 4,
    blocks: [
      tip('info', 'Ezberlenen', `Akort aleti "tam" diyorsa gitar akortludur.`),
      p(
        `İki sesin frekansları arasındaki oran basit bir sayı olduğunda, örneğin 2:1 (oktav), 3:2 (tam 5'li) ya da 5:4 (büyük 3'lü), titreşimleri düzenli aralıklarla örtüşür ve aralık pürüzsüz duyulur. Böyle akort edilen aralıklara **saf aralık** denir.`,
      ),
      p(
        `Gitarın perdeleri ise oktavı **12 eşit** yarım sese böler. Buna **eşit tamperaman** denir. Büyük avantajı, aynı kalıbı her tonda çalabilmen. Bedeli: oktav dışındaki aralıklar saf hâllerinden biraz sapar.`,
      ),
      table(
        ['Aralık', 'Gitarda (sent)', 'Saf (sent)', 'Fark'],
        [
          ['Oktav', '1200', '1200', '0'],
          [`Tam 5'li`, '700', '702', '2 sent dar'],
          [`Tam 4'lü`, '500', '498', '2 sent geniş'],
          [`Büyük 3'lü`, '400', '386', '14 sent geniş'],
        ],
      ),
      p(
        `Tam 5'li ve 4'lüdeki fark neredeyse duyulmaz. Büyük 3'lüdeki 14 sent ise dikkatli bir kulağa hafif bir dalgalanma, yani **vuruş** olarak gelir. Saf 3'lüde bu vuruş yoktur.`,
      ),
      compare([
        [`Gitardaki büyük 3'lü`, { harm: 'E3 G#3' }],
        [`Saf büyük 3'lü`, { harm: 'E3 G#3-14c' }],
      ]),
      {
        t: 'keyboard',
        from: 'E3',
        to: 'E4',
        highlight: 'E3 G#3',
        caption: `{{pc:E}} ile {{pc:G#}} arası 4 yarım ses: büyük 3'lü.`,
      },
      h(`Bir akoru düzeltmek ötekini bozar`),
      p(
        `Bazı gitaristler {{c:E}} akorunun 3'lüsünü veren 3. teli kulakla çok az gevşetir; akor tatlanır. Ama aynı tel {{c:C}} akorunda 5'li görevi görür ve bu kez o akor bozulur. Eşit tamperaman, her tonun biraz ödün verdiği bir uzlaşmadır.`,
      ),
      compare([
        [`{{c:E}}, gitardaki gibi`, { harm: 'E2 B2 E3 G#3 B3 E4' }],
        [`{{c:E}}, 3. tel 14 sent gevşek`, { harm: 'E2 B2 E3 G#3-14c B3 E4' }],
        [`{{c:C}}, gitardaki gibi`, { harm: 'C3 E3 G3 C4 E4' }],
        [`{{c:C}}, 3. tel 14 sent gevşek`, { harm: 'C3 E3 G3-14c C4 E4' }],
      ]),
      tip(
        'success',
        'Akort aleti yanılmıyor',
        `Akort aleti tellerini eşit tampere göre ayarlar. Bir akorda duyduğun hafif pürüz senin hatan değil, sistemin doğası.`,
      ),
      go('Majör ve minör dersi', 'akor.ders.a2'),
      go('Akort aletini aç', 'araclar.akort'),
    ],
  },
  // Sources:
  // - A string's frequency depends on its vibrating length, tension and mass per unit length (Mersenne's laws,
  //   f = (1/2L) sqrt(T/mu)): https://en.wikipedia.org/wiki/Mersenne%27s_laws ;
  //   https://hyperphysics.gsu.edu/hbase/Waves/string.html ; https://newt.phys.unsw.edu.au/jw/strings.html
  // - Guitar strings have similar lengths and tensions, so low strings get their pitch from extra mass; the fat
  //   strings are wound: https://en.wikipedia.org/wiki/Mersenne%27s_laws ; https://newt.phys.unsw.edu.au/jw/strings.html
  // - Bass strings are set slightly longer at the saddle: https://en.wikipedia.org/wiki/Guitar ;
  //   https://newt.phys.unsw.edu.au/jw/strings.html
  // - E2 about 82 Hz, E4 about 330 Hz: https://www.acs.psu.edu/drussell/guitars/es-335.html ;
  //   https://en.wikipedia.org/wiki/Guitar_tunings
  // - "An octave up needs four times the tension" follows from f ~ sqrt(T) (the same formula).
  {
    id: 'kalin-ince-teller',
    topic: 'gitar',
    title: 'Teller aynı boyda; sesleri neden bu kadar farklı?',
    teaser: `Bir telin sesi boyuna, gerginliğine ve ağırlığına bağlıdır. Gitarda boy ve gerginlik birbirine yakın olduğu için iş ağırlığa kalır.`,
    minutes: 3,
    blocks: [
      tip('info', 'Ezberlenen', `Kalın teller kalın, ince teller ince ses verir.`),
      p(
        `Gerili bir telin titreşim hızı üç şeye bağlıdır: **titreşen boy** (kısaldıkça ses incelir), **gerginlik** (arttıkça ses incelir) ve telin **ağırlığı**, yani bir santimetresinin kütlesi (arttıkça ses kalınlaşır).`,
      ),
      p(
        `Gitarın altı teli neredeyse aynı boydadır. Gerginlikleri de birbirine yakındır; yoksa bazı teller basılamayacak kadar sert, bazıları çınlamayacak kadar gevşek olurdu. Geriye ağırlık kalır: kalın teller daha ağırdır ve daha yavaş titreşir. En kalın tellerin üstüne bu yüzden tel sarılır; sarım, teli bükülmesi zorlaşmadan ağırlaştırır.`,
      ),
      compare([
        ['6. tel: {{n:E2}}, saniyede yaklaşık 82 titreşim', { seq: 'E2:2' }],
        ['1. tel: {{n:E4}}, saniyede yaklaşık 330 titreşim', { seq: 'E4:2' }],
      ]),
      {
        t: 'fretboard',
        notes: 'E2 E4',
        from: 0,
        to: 5,
        caption: `İki {{pc:E}} teli: aynı boy, iki oktav fark. 1. tel 6. telden dört kat hızlı titreşir.`,
      },
      h(`Akort ederken ne değişir?`),
      p(
        `Burguyu çevirdiğinde değişen gerginliktir. Ses, gerginliğin karekökü oranında yükselir: bir teli bir oktav yukarı akort etmek için gerginliğini **dört katına** çıkarman gerekir. Tel buna dayanmaz; bu yüzden her ses bölgesi için ayrı kalınlıkta tel kullanılır.`,
      ),
      tip(
        'success',
        'Kendin dene',
        `Bir teli 12. perdeye bastığında titreşen boyunu yarıya indirir ve bir oktav yukarı çıkarsın. Aynı sonuca burguyla ulaşmak teli koparırdı.`,
      ),
      go('Gitarın boş telleri dersi', 'nota.ders.n3'),
      go('Akort aletini aç', 'araclar.akort'),
    ],
  },
  // Sources:
  // - AEU theory: 53 Holdrian commas per octave (about 22.64 cents each), a whole tone (tanini) is 9 commas;
  //   koma 1, bakiye 4, küçük mücenneb 5, büyük mücenneb 8, tanini 9: https://en.wikipedia.org/wiki/Turkish_makam ;
  //   https://tymm.meb.gov.tr/muzik-okullari-muziksel-isitme-okuma-yazma-dersi/unite/1062 ;
  //   https://en.wikipedia.org/wiki/Holdrian_comma ; https://archives.ismir.net/ismir2013/paper/000119.pdf
  // - Practice differs from the AEU grid: https://avesis.istanbul.edu.tr/yayin/e37686f0-4d04-4bdd-b66c-daaf9d4713c9/turk-makam-muziginde-nazariyat-icra-ortusmezligine-bir-cozum ;
  //   https://archives.ismir.net/ismir2013/paper/000119.pdf
  // - Segâh is B (bûselik) lowered by one comma, an in-between tone: https://islamansiklopedisi.org.tr/segah ;
  //   https://en.wikipedia.org/wiki/Turkish_makam (segâh 48 commas, bûselik 49). B -19 cents vs 12-TET from A is computed.
  // - In Uşşak (on dügâh, A) segâh is pressed 1-2 commas lower when descending, 6-7 commas above A (B -64 to
  //   -42 cents, computed): https://islamansiklopedisi.org.tr/ussak ; https://dergipark.org.tr/tr/pub/folklor/article/1476669
  //   (bağlama frets measured about 40 cents below 12-TET B, C#, E, F#)
  // - The bağlama has 17 frets per octave, 12 plus 5 comma frets (e.g. between B-flat and B):
  //   https://dergipark.org.tr/en/download/article-file/1523486 ; https://dergipark.org.tr/en/download/article-file/1431859 ;
  //   https://en.wikipedia.org/wiki/Ba%C4%9Flama
  // - Erkan Oğur made his first fretless guitar in 1976 and pioneered it in Turkey: https://en.wikipedia.org/wiki/Erkan_O%C4%9Fur ;
  //   https://bianet.org/haber/erkan-ogur-muzik-okulu-aciliyor-228448 ;
  //   https://polen.itu.edu.tr/items/16aeef2f-2e27-452e-983d-71d264750452 ; https://dergipark.org.tr/tr/pub/folklor/article/1476669
  // - The first fretless classical guitar attempt in Turkey was Mutlu Torun's, 1971:
  //   https://polen.itu.edu.tr/items/16aeef2f-2e27-452e-983d-71d264750452 ; https://dergipark.org.tr/tr/pub/folklor/article/1476669
  // - Tolgahan Çoğulu designed the Adjustable Microtonal Guitar with movable frets in 2008:
  //   https://en.wikipedia.org/wiki/Tolgahan_%C3%87o%C4%9Fulu ;
  //   https://sustainability.itu.edu.tr/conveying-the-past-to-the-future-traditional-arts-are-alive-with-itu ;
  //   https://newatlas.com/music/lego-microtonal-guitar/
  // - Bending reaches pitches between frets: https://en.wikipedia.org/wiki/String_bending ;
  //   https://dergipark.org.tr/tr/pub/folklor/article/1476669
  // - Hicaz tetrachord S + A12 + S (5 + 12 + 5 commas); the augmented second between dik kürdî and nîm hicaz
  //   defines it: https://tymm.meb.gov.tr/muzik-okullari-muziksel-isitme-okuma-yazma-dersi/unite/1073 ;
  //   https://en.wikipedia.org/wiki/Hicaz_pitch_class_set ; https://islamansiklopedisi.org.tr/humayun--musiki
  //   (B-flat +13, C# -15, D -2 cents vs 12-TET from A are computed)
  {
    id: 'koma-sesler',
    topic: 'gitar',
    title: 'Bazı makam sesleri gitarda neden yok?',
    teaser: `Gitarın perdeleri oktavı 12 yarım sese böler. Türk makam müziğinde ise iki perdenin arasına düşen sesler var.`,
    minutes: 5,
    blocks: [
      tip('info', 'Ezberlenen', `Müzikteki en küçük adım yarım sestir.`),
      p(
        `Gitarda iki komşu perde arasındaki mesafe yarım sestir ve bir oktavda 12 yarım ses vardır. Türk makam müziğinin okullarda öğretilen kuramı (Arel–Ezgi–Uzdilek sistemi) ise daha ince böler: bir tam ses 9 **koma**ya ayrılır ve bir koma yaklaşık 23 sent eder.`,
      ),
      table(
        ['Aralık', 'Koma', 'Yaklaşık sent', `{{pc:A}}'dan dinle`],
        [
          ['Koma', '1', '23', cell({ glide: 'A3:1.5 A3+22.6c:1.5', bpm: 60 })],
          ['Bakiye', '4', '91', cell({ glide: 'A3:1.5 A3+90.6c:1.5', bpm: 60 })],
          ['Küçük mücenneb', '5', '113', cell({ glide: 'A3:1.5 A#3+13.2c:1.5', bpm: 60 })],
          ['Büyük mücenneb', '8', '181', cell({ glide: 'A3:1.5 B3-18.9c:1.5', bpm: 60 })],
          ['Tanini (tam ses)', '9', '204', cell({ glide: 'A3:1.5 B3+3.8c:1.5', bpm: 60 })],
        ],
      ),
      p(
        `Gitardaki yarım ses 100 sent olduğu için bu aralıkların çoğu iki perdenin arasına düşer. En bilinen örnek **segâh** perdesidir: kuramda {{pc:B}}'nin bir koma pesidir, bir ana ses değil ara sestir. {{pc:A}} üzerindeki Uşşak makamında inerken segâh 1–2 koma daha pes basılır ve {{pc:Bb}} ile {{pc:B}}'nin aşağı yukarı ortasına düşer.`,
      ),
      compare([
        ['{{pc:Bb}}', { glide: 'Bb3:3', bpm: 60 }],
        ['Segâh, kuramdaki yeri', { glide: 'B3-19c:3', bpm: 60 }],
        [`Segâh, Uşşak'ta inerken`, { glide: 'B3-50c:3', bpm: 60 }],
        ['{{pc:B}}', { glide: 'B3:3', bpm: 60 }],
      ]),
      {
        t: 'keyboard',
        from: 'A3',
        to: 'C4',
        highlight: 'A#3 B3',
        caption: `Piyanoda ve perdeli gitarda bu iki ses arasında başka ses yok. Örnekteki Uşşak segâhı tam ortada çalınıyor; kayıtlarda yeri makama ve icracıya göre değişir.`,
      },
      p(
        `Kuram ile icra da her zaman örtüşmez: usta icracıların perdeleri kayıtlarda kuramdaki yerlerinden farklı çıkar ve bu bilgi büyük ölçüde kulaktan kulağa aktarılır.`,
      ),
      h(`Bağlama, perdesiz gitar, mikrotonal gitar`),
      list([
        `**Bağlama:** Sapında bir oktavda 17 perde vardır: 12 yarım sese ek olarak 5 koma perdesi. Bu ek perdeler örneğin {{pc:Bb}} ile {{pc:B}} arasına düşer.`,
        `**Perdesiz gitar:** Erkan Oğur ilk perdesiz gitarını 1976'da yaptı ve Türkiye'de perdesiz gitarın öncüsü oldu. Türkiye'deki ilk perdesiz klasik gitar denemesi ise 1971'de Mutlu Torun'unkiydi.`,
        `**Mikrotonal gitar:** Tolgahan Çoğulu 2008'de perdeleri kanallar içinde kaydırılabilen, istenen yere ek perde takılabilen Ayarlanabilir Mikrotonal Gitar'ı tasarladı.`,
        `**Bükme (bending):** Perdeli gitarda teli bükerek iki perde arasındaki seslere ulaşabilirsin.`,
      ]),
      play('{{pc:Bb}} perdesinden bükerek araya çıkmak', { glide: 'Bb3:1.5 /B3-50c B3-50c:2', bpm: 60 }),
      h(`Hicaz: gitarda yaklaşık çalınır`),
      p(
        `Hicaz makamının karakteri, 2. ile 3. derecesi arasındaki **artık ikili** aralıktan gelir: kuramda 12 koma, yani bir tam sesten geniş. Gitarda bu aralık 3 yarım sesle çalınır; {{pc:A}} üzerinde {{pc:A}}–{{pc:Bb}}–{{pc:C#}}–{{pc:D}} dizisi Hicaz'ın ilk dört sesine çok yakındır, ama tam üstüne oturmaz.`,
      ),
      compare([
        ['Gitarda', { seq: 'A3 Bb3 C#4 D4:2', bpm: 80 }],
        [`Kuramdaki yerleri`, { glide: 'A3 Bb3+13c C#4-15c D4-2c:2', bpm: 80 }],
      ]),
      go('Diyez ve bemol dersi', 'nota.ders.n2'),
      go('Kromatik dokunuşlar', 'gitar.egz.7'),
    ],
  },
];
